import React from "react";

import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage, RequiredMark } from "@components/inputs/shared";
import useCurrency from "@state/hooks/shared/useCurrency";
import classNames from "classnames";

import { FormInputFieldCurrencyProps } from "./types";

export default function FormInputFieldCurrency(
	props: FormInputFieldCurrencyProps
) {
	const {
		// Element type
		type = "text",

		// Identifiers
		id,
		name,
		label,

		// Validation & behavior
		required = false,
		validate,
		pattern,
		errorMessage = undefined,

		// Input-specific
		placeholder = undefined,

		// Styling
		className,
		labelWrapperClassName = undefined,
		labelClassName,
		inputWrapperClassName = undefined,
		inputFieldClassName = undefined,
		inputClassName = undefined,
		currencyWrapperClassName = undefined,
		validationClassName = undefined,

		// Data / content
		providedCurrency,

		// Modifiers
		isInline = false,

		// Remaining props
		...rest
	} = props;

	const form = useFormContext();
	const field = form.register(props.name, {
		required: {
			value: required,
			message: errorMessage || "COMMON.REQUIRED_FIELD",
		},
		validate,
		pattern,
	});

	const { t } = useTranslation();
	const { currency } = useCurrency();

	const labelClasses = classNames("label", labelClassName);

	const inputClasses = classNames(
		"form__field--icon__input",
		inputClassName,
		{
			"input input--med":
				inputClassName == undefined || inputClassName == null,
		}
	);

	const inputId = id || `id--${name}--${Math.floor(Math.random() * 100)}`;

	if (isInline) {
		return (
			<div className={className}>
				<div className={labelWrapperClassName}>
					<label
						htmlFor={inputId}
						className={labelClassName || "label"}
					>
						{t(label)}
						<RequiredMark required={required} />
					</label>
				</div>

				<div className={inputWrapperClassName}>
					<div className={inputFieldClassName}>
						{/* @ts-expect-error - error because defaultValue is number*/}
						<input
							type={type}
							id={inputId}
							placeholder={t(placeholder || "")}
							className={inputClassName}
							{...field}
							{...rest}
						/>

						<span className={currencyWrapperClassName}>
							{providedCurrency != null
								? providedCurrency
								: currency}
						</span>
					</div>

					<ErrorMessage
						name={name}
						addClassName={validationClassName}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className={className}>
			<label htmlFor={inputId} className={labelClasses}>
				{t(label)}
				<RequiredMark required={required} />
			</label>

			<ErrorMessage name={name} addClassName={validationClassName} />

			<div className="form__field--icon">
				{/* @ts-expect-error - error because defaultValue is number*/}
				<input
					type={type}
					id={inputId}
					placeholder={t(placeholder || "")}
					className={inputClasses}
					{...field}
					{...rest}
				/>

				<span className="form__field--currency__symbol">
					{providedCurrency != null ? providedCurrency : currency}
				</span>
			</div>
		</div>
	);
}
